import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { MatIconModule } from '@angular/material/icon';
import { merge, Subject, takeUntil } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { cloneDeep } from 'lodash-es';
import { AccordionFieldConfig } from './accordion.types';

/**
 * Structural `accordion` field type — a fixed (non-repeat) group of fields rendered inside a
 * flat, collapsible panel (Figma "Operational details (optional)"). Mirrors the `group` type:
 * the accordion key counts as a keyPath segment and its child fields render through
 * `field.fieldGroup`. Collapsed by default; `props.expanded` opens it on load.
 */
@Component({
  selector: 'formly-field-accordion',
  template: `
    <div class="border rounded-lg mb-3 w-full">
      <!--
        Figma "Accordion": a 16px inset all round, a 40px right gutter on the text column so the
        description wraps well before the chevron, and title + description both at 14px.
      -->
      <button
        type="button"
        class="dd-accordion-header flex w-full items-start justify-between gap-4 rounded-lg px-4 py-4 text-left select-none"
        [attr.aria-expanded]="expanded()"
        (click)="toggle()">
        <span class="min-w-0 flex-1 pr-10">
          @if (props.label) {
            <span class="flex flex-wrap items-center gap-2">
              <span class="text-[14px] font-medium leading-[1.4]">{{ props.label }}</span>
              <!--
                Figma "Completed" chip: confirms an optional group was filled in without having to
                open it. Only meaningful for groups that carry input, so it stays hidden while the
                group is empty.
              -->
              @if (completed()) {
                <!--
                  Same tokens as every other positive badge in the app (Figma
                  background/default/positive + foreground/positive/primary), so this chip cannot
                  drift from the status chips in the tables — and it follows dark mode for free.
                  Radius 8px and 12px/500 text come straight from the badge component.
                -->
                <span
                  class="inline-flex items-center gap-1 rounded-lg bg-[var(--dd-badge-positive-bg)] px-2 py-1 text-[12px] font-medium leading-none text-[color:var(--dd-badge-positive-fg)]"
                  data-cy="accordion-completed">
                  <mat-icon class="icon-size-3.5" svgIcon="heroicons_solid:check"></mat-icon>
                  Voltooid
                </span>
              }
            </span>
          }
          @if (props.description) {
            <span class="mt-1.5 block text-[14px] leading-[1.5] text-accent">{{ props.description }}</span>
          }
        </span>
        <mat-icon
          class="icon-size-5 shrink-0 text-accent transition-transform duration-200"
          [class.rotate-180]="expanded()"
          svgIcon="heroicons_outline:chevron-down"></mat-icon>
      </button>
      @if (expanded()) {
        <!--
          Separator under the header, open state only (Figma border/separator). It marks where the
          summary ends and the editable content starts; a closed panel has nothing to separate.

          flow-root, not a plain block: the form-builder gives every child field a float-left
          className, and a block box does not grow to contain floats. Without it this wrapper
          collapses to 0 and the panel border closes above its own fields, which then spill out
          below it. flow-root establishes a block formatting context without clipping, so
          dropdowns and tooltips still escape the panel.
        -->
        <div class="flow-root border-t border-[color:var(--dd-accordion-separator)] px-4 pb-4 pt-4">
          @for (fieldItem of field.fieldGroup; track $index) {
            <formly-field [field]="fieldItem"></formly-field>
          }
        </div>
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;

      /* Figma border/separator. One step stronger than the panel's own border so the line reads as
         a divider rather than a second edge; dark takes the design's border/secondary. */
      --dd-accordion-separator: #c7c7cc;
    }

    :host-context(.dark) {
      --dd-accordion-separator: #545559;
    }

    /*
      The base layer kills the UA focus ring globally ("* { &:focus { outline: none !important } }"
      in tailwind.scss), and this header is a native button styled from scratch, so it had no
      keyboard affordance at all. Hover was missing too: a full-width click target with no
      pre-click feedback reads as static text, especially while the panel is collapsed.
    */
    .dd-accordion-header {
      transition: background-color 150ms ease;
    }

    .dd-accordion-header:hover {
      background-color: rgba(0, 0, 0, 0.02);
    }

    :host-context(.dark) .dd-accordion-header:hover {
      background-color: rgba(255, 255, 255, 0.04);
    }

    .dd-accordion-header:focus-visible {
      outline: 2px solid var(--fuse-primary) !important;
      outline-offset: -2px;
    }
  `,
  imports: [FormlyModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyAccordionComponent extends FieldType<AccordionFieldConfig> implements OnInit, OnDestroy {
  readonly #expanded = signal(false);
  readonly expanded = this.#expanded.asReadonly();

  /** True once this accordion holds user-supplied content and none of its controls is invalid. */
  readonly completed = signal(false);

  /**
   * The group's value as it stood on the first emission, i.e. including the structural defaults the
   * field widgets seed themselves (an address' `geoRadius`, a date offset's unit). Numeric and
   * boolean leaves are only treated as content once they differ from this baseline, so an untouched
   * optional group never claims to be completed.
   */
  #baseline: unknown;

  readonly #destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.#expanded.set(this.props.expanded ?? false);

    // The accordion has its own FormGroup (it is a keyPath segment), so its value stream covers
    // every child field regardless of nesting.
    // Validity is half the completed test, and it moves without the value moving: an async
    // validator resolving, or a hide-expression revealing a required control. Merge the status
    // stream in so the badge cannot get stuck on a group that has since become invalid.
    const control = this.formControl;
    if (!control) {
      return;
    }
    merge(control.valueChanges, control.statusChanges)
      .pipe(startWith(null), takeUntil(this.#destroy$))
      .subscribe(() => {
        const value = control.value;
        if (this.#baseline === undefined) {
          this.#baseline = cloneDeep(value);
        }
        this.completed.set(this.#isCompleted(value));
      });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  toggle(): void {
    this.#expanded.update(value => !value);
  }

  /**
   * Completed = the group is valid AND carries user-supplied content. Both halves matter: an
   * optional group is valid while empty, so validity alone would mark every group completed.
   */
  #isCompleted(value: unknown): boolean {
    const control = this.formControl;
    if (!control || control.invalid) {
      return false;
    }
    return this.#hasContent(value, this.#baseline);
  }

  /**
   * Content = a leaf the user could have supplied: a non-empty string, a date, a longer collection,
   * or a number/boolean that has moved away from the baseline.
   *
   * Numbers and booleans are judged against the baseline rather than by emptiness because the field
   * widgets seed their own defaults — an address arrives as `{ fullAddress: '', geoRadius: 100 }`,
   * which must not read as filled in. A leaf with NO baseline entry (a control that appeared after
   * the first emission: a group revealed by a hide-expression, a repeater row the user added) is
   * treated the same way, since its default arrives together with the control; the strings in that
   * same group still mark it completed once the user types.
   *
   * Trade-off: a group whose only content is numeric and was loaded from the server sits in the
   * baseline already, so it stays un-badged. Under-reporting beats a badge that claims an untouched
   * group is done.
   */
  #hasContent(value: unknown, baseline: unknown): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    if (value instanceof Date) {
      return true;
    }
    if (Array.isArray(value)) {
      const base = Array.isArray(baseline) ? baseline : [];
      // A row the user added is content in itself; positional comparison would otherwise line rows
      // up against the wrong baseline entry after an insert or a delete.
      if (value.length !== base.length) {
        return true;
      }
      return value.some((item, index) => this.#hasContent(item, base[index]));
    }
    if (typeof value === 'object') {
      const base = (baseline ?? {}) as Record<string, unknown>;
      return Object.entries(value as Record<string, unknown>).some(([key, item]) =>
        this.#hasContent(item, base?.[key])
      );
    }
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    return baseline !== undefined && value !== baseline;
  }
}
